import React, { useState } from "react";
import classNames from "classnames";
import { FinanceStore, reconciliationStats, expectsStatement } from "./finance-store";
import { STATEMENT_SOURCES, StatementSource, PeriodKey, periodRange, inRange, money, fDateTime, channelToSource, downloadCsv, todayIso, addDaysIso } from "./finance-data";
import { FinPage, Kpi, Card, PeriodPicker, btnPrimary, btnGhost, Toast } from "./finance-ui";

// Matches operator statements (Orange Money, MTN MoMo, InTouch, bank) against internal transactions.
export default function FinanceReconciliation({ store }: { store: FinanceStore }) {
  const { txs, statement, links, setLinks, notes, setNotes } = store;
  const [source, setSource] = useState<"Tous" | StatementSource>("Tous");
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const [from, setFrom] = useState(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState(todayIso());
  const [lineId, setLineId] = useState<string | null>(null);
  const [txId, setTxId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const range = periodRange(period, from, to);
  const linked = new Set(Object.values(links));
  const txByRef = new Map(txs.map((t) => [t.opRef, t]));
  const inScope = (src: StatementSource | null, date: string) => (source === "Tous" || src === source) && inRange(date, range);

  const openLines = statement.filter((l) => !links[l.id] && !notes[l.id] && inScope(l.source, l.date));
  const openTxs = txs.filter((t) => expectsStatement(t) && !linked.has(t.id) && inScope(channelToSource(t.channel), t.date));
  const statementRefs = new Set(statement.map((l) => l.opRef));
  const lineKind = (l: (typeof statement)[number]) => {
    const t = txByRef.get(l.opRef);
    if (!t || linked.has(t.id)) return { label: "Non identifié", tone: "bg-rose-50 text-rose-700", t: undefined };
    if (t.amount !== l.amount) return { label: `Écart ${money(l.amount - t.amount)}`, tone: "bg-amber-50 text-amber-700", t };
    return { label: "Correspondance exacte", tone: "bg-emerald-50 text-emerald-700", t };
  };
  const exact = openLines.filter((l) => lineKind(l).label === "Correspondance exacte");
  const stats = reconciliationStats(store);

  const selLine = statement.find((l) => l.id === lineId);
  const selTx = txs.find((t) => t.id === txId);
  const diff = selLine && selTx ? selLine.amount - selTx.amount : 0;
  const suggested = selLine ? txByRef.get(selLine.opRef) : undefined;
  const sortedTxs = suggested ? [...openTxs].sort((a, b) => (a.id === suggested.id ? -1 : b.id === suggested.id ? 1 : 0)) : openTxs;

  const autoMatch = () => {
    const next = { ...links };
    exact.forEach((l) => (next[l.id] = lineKind(l).t!.id));
    setLinks(next);
    flash(`${exact.length} ligne(s) rapprochée(s) automatiquement`);
  };
  const link = () => {
    if (!selLine || !selTx) return;
    if (diff !== 0 && !note.trim()) return flash("Justifie l'écart de montant avant de rapprocher.");
    setLinks({ ...links, [selLine.id]: selTx.id });
    if (diff !== 0) setNotes({ ...notes, [selLine.id]: { kind: "Écart justifié", text: note.trim() } });
    setLineId(null);
    setTxId(null);
    setNote("");
    flash(`Rapproché : ${selLine.opRef} ↔ ${selTx.ref}`);
  };
  const markLine = (kind: "Écart justifié" | "Litige") => {
    if (!selLine) return;
    if (!note.trim()) return flash(kind === "Litige" ? "Décris le litige (motif, contact opérateur…)." : "Indique la justification (ex. paiement client à affecter).");
    setNotes({ ...notes, [selLine.id]: { kind, text: note.trim() } });
    setLineId(null);
    setNote("");
    flash(kind === "Litige" ? "Litige ouvert auprès de l'opérateur" : "Ligne justifiée");
  };

  const done = statement
    .filter((l) => (links[l.id] || notes[l.id]) && inScope(l.source, l.date))
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <FinPage
      title="Réconciliation"
      subtitle="Rapprochement des relevés Orange Money, MTN MoMo, InTouch et bancaires avec les transactions de la plateforme"
      actions={
        <>
          <button
            type="button"
            className={btnGhost}
            onClick={() =>
              downloadCsv(
                `reconciliation_${range[0]}_${range[1]}.csv`,
                ["Date", "Source", "Réf. opérateur", "Montant relevé", "Transaction", "Montant transaction", "Statut", "Note"],
                statement
                  .filter((l) => inScope(l.source, l.date))
                  .map((l) => {
                    const t = links[l.id] ? txs.find((x) => x.id === links[l.id]) : undefined;
                    return [l.date.replace("T", " "), l.source, l.opRef, l.amount, t?.ref ?? "", t?.amount ?? "", links[l.id] ? "Rapprochée" : notes[l.id]?.kind ?? "À traiter", notes[l.id]?.text ?? ""];
                  })
              )
            }
          >
            Exporter l'état (CSV)
          </button>
          <button type="button" onClick={autoMatch} disabled={!exact.length} className={btnPrimary}>
            Rapprochement automatique ({exact.length})
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <Kpi label="Taux de rapprochement" value={`${stats.rate} %`} sub={`${statement.length} lignes de relevé`} tone={stats.rate >= 90 ? "#047857" : "#b45309"} />
        <Kpi label="Correspondances exactes" value={String(stats.pendingAuto)} sub="prêtes pour le rapprochement auto" tone="#047857" />
        <Kpi label="Écarts de montant" value={String(stats.gaps)} sub="référence trouvée, montant différent" tone="#b45309" />
        <Kpi label="Non identifiés" value={String(stats.unidentified)} sub="sur le relevé, absents de la plateforme" tone="#b91c1c" />
        <Kpi label="Absentes des relevés" value={String(stats.missing)} sub="transactions sans ligne opérateur" tone="#b91c1c" />
        <Kpi label="Litiges ouverts" value={String(Object.values(notes).filter((n) => n.kind === "Litige").length)} sub="auprès des opérateurs" />
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-3">
        <div className="flex p-[3px] bg-[#f3ece2] rounded-[10px] gap-[3px]">
          {(["Tous", ...STATEMENT_SOURCES] as const).map((s) => (
            <button key={s} type="button" onClick={() => setSource(s)} className={classNames("px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap", source === s ? "bg-white text-[#78350f] shadow-xs" : "text-[#8a7b6b]")}>
              {s}
            </button>
          ))}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title={`Relevé opérateur · à traiter (${openLines.length})`}>
          <div className="max-h-[420px] overflow-y-auto px-2 pb-2">
            {openLines.map((l) => {
              const k = lineKind(l);
              const on = lineId === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => {
                    setLineId(on ? null : l.id);
                    setTxId(on ? null : k.t?.id ?? null);
                  }}
                  className={classNames("w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border mb-1 text-xs", on ? "border-[#92400e] bg-[#fdf6ec]" : "border-transparent hover:bg-[#fdf8f1]")}
                >
                  <span className="w-20 shrink-0 text-gray-500">{fDateTime(l.date)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono font-semibold truncate">{l.opRef}</span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {l.source} · {l.label}
                    </span>
                  </span>
                  <span className={classNames("px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap", k.tone)}>{k.label}</span>
                  <span className="w-24 text-right font-bold whitespace-nowrap">{money(l.amount)}</span>
                </button>
              );
            })}
            {openLines.length === 0 && <div className="p-6 text-center text-xs text-gray-500">Tout est rapproché sur cette période.</div>}
          </div>
        </Card>

        <Card title={`Transactions plateforme · non rapprochées (${openTxs.length})`}>
          <div className="max-h-[420px] overflow-y-auto px-2 pb-2">
            {sortedTxs.map((t) => {
              const on = txId === t.id;
              const absent = !statementRefs.has(t.opRef);
              return (
                <button key={t.id} type="button" onClick={() => setTxId(on ? null : t.id)} className={classNames("w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border mb-1 text-xs", on ? "border-[#92400e] bg-[#fdf6ec]" : "border-transparent hover:bg-[#fdf8f1]")}>
                  <span className="w-20 shrink-0 text-gray-500">{fDateTime(t.date)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono font-semibold truncate">
                      {t.ref} <span className="text-gray-400 font-normal">· {t.opRef}</span>
                    </span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {t.channel} · {t.client} · {t.dossier}
                    </span>
                  </span>
                  {suggested?.id === t.id && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#ede9fe] text-[#5b21b6]">Suggérée</span>}
                  {absent && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 whitespace-nowrap">Absente du relevé</span>}
                  <span className="w-24 text-right font-bold whitespace-nowrap">{money(t.amount)}</span>
                </button>
              );
            })}
            {openTxs.length === 0 && <div className="p-6 text-center text-xs text-gray-500">Aucune transaction en attente de rapprochement.</div>}
          </div>
        </Card>
      </div>

      {/* Action bar for the current selection */}
      <div className={classNames("mt-3 p-3.5 rounded-xl border flex items-center gap-3 flex-wrap text-xs", selLine || selTx ? "bg-white border-[#e7c9a0]" : "bg-[#fbf8f4] border-dashed border-[#e7dccd] text-gray-500")}>
        {!selLine && !selTx ? (
          <span>Sélectionne une ligne de relevé et une transaction pour les rapprocher, ou une ligne seule pour la justifier ou ouvrir un litige.</span>
        ) : (
          <>
            <span className="font-semibold">
              {selLine ? `${selLine.opRef} (${money(selLine.amount)})` : "Aucune ligne de relevé"} ↔ {selTx ? `${selTx.ref} (${money(selTx.amount)})` : "aucune transaction"}
            </span>
            {selLine && selTx && diff !== 0 && <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-bold">Écart {money(diff)} — justification obligatoire</span>}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Justification / motif" className="flex-1 min-w-[200px] h-9 px-3 border border-[#e7dccd] rounded-lg" />
            {selLine && !selTx && (
              <>
                <button type="button" onClick={() => markLine("Écart justifié")} className={btnGhost}>
                  Justifier sans transaction
                </button>
                <button type="button" onClick={() => markLine("Litige")} className="h-9 px-3.5 border border-rose-200 bg-rose-50 rounded-lg font-semibold text-rose-700">
                  Ouvrir un litige
                </button>
              </>
            )}
            <button type="button" disabled={!selLine || !selTx} onClick={link} className={btnPrimary}>
              Rapprocher
            </button>
          </>
        )}
      </div>

      <Card title={`Traitées sur la période (${done.length})`} className="mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[760px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#8a7b6b]">
                {["Date", "Source", "Réf. opérateur", "Montant relevé", "Transaction", "Statut", ""].map((h) => (
                  <th key={h} className="px-4 py-2 font-bold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {done.slice(0, 25).map((l) => {
                const t = links[l.id] ? txs.find((x) => x.id === links[l.id]) : undefined;
                const n = notes[l.id];
                return (
                  <tr key={l.id} className="border-t border-[#f5efe6]">
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fDateTime(l.date)}</td>
                    <td className="px-4 py-2">{l.source}</td>
                    <td className="px-4 py-2 font-mono">{l.opRef}</td>
                    <td className="px-4 py-2 font-semibold whitespace-nowrap">{money(l.amount)}</td>
                    <td className="px-4 py-2 font-mono text-[#92400e]">{t?.ref ?? "—"}</td>
                    <td className="px-4 py-2">
                      <span className={classNames("px-2 py-0.5 rounded font-bold text-[11px]", n?.kind === "Litige" ? "bg-rose-50 text-rose-700" : n ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")} title={n?.text}>
                        {t ? (n ? "Rapprochée avec écart" : "Rapprochée") : n?.kind}
                      </span>
                      {n && <div className="text-[11px] text-gray-500 mt-0.5 max-w-[220px] truncate">{n.text}</div>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          const nl = { ...links };
                          delete nl[l.id];
                          const nn = { ...notes };
                          delete nn[l.id];
                          setLinks(nl);
                          setNotes(nn);
                          flash("Rapprochement annulé");
                        }}
                        className="text-[11px] font-semibold text-gray-500 hover:text-rose-700"
                      >
                        Annuler
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {done.length > 25 && <div className="px-4 py-2 text-[11px] text-gray-500">25 plus récentes affichées sur {done.length} — l'export CSV contient tout.</div>}
        </div>
      </Card>
      <Toast msg={toast} />
    </FinPage>
  );
}
